import { useEffect, useRef, useState } from 'react';
import type { HeadPreset } from '../api/types';
import { useT } from '../i18n';
import { useLive } from '../live/store';
import { useStoredState } from '../lib/hooks';
import { sendCmd } from '../lib/robot';
import { IconMuted, IconSpeaker } from './Icon';
import { useCatalog } from '../api/queries';
import { Select } from './Select';

const PRESETS: { id: HeadPreset; angle: number }[] = [
  { id: 'adult', angle: 180 },
  { id: 'teen', angle: 205 },
  { id: 'child', angle: 245 },
  { id: 'straight', angle: 195 },
  { id: 'down', angle: 270 },
];

/**
 * Speaker volume (PROTOCOL §16): slider 0–100 % → `setVolume {level}` debounced 250 ms; shows status.volume;
 * Mute remembers the previous level per robot.
 */
export function VolumeControl({ serial, online }: { serial: string; online: boolean }) {
  const t = useT();
  const vol = useLive((s) => s.robots[serial]?.status?.volume);
  const [draft, setDraft] = useState<number | null>(null);
  const [lastLevel, setLastLevel] = useStoredState<Record<string, number>>('cruzr.lastVolume', {});
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const settle = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
      if (settle.current) clearTimeout(settle.current);
    },
    [],
  );
  const media = typeof vol?.media === 'number' ? Math.round(vol.media) : null;
  const shown = draft ?? media ?? 50;
  const muted = media === 0 && draft === null;

  const send = (level: number, immediate = false) => {
    setDraft(level);
    if (timer.current) clearTimeout(timer.current);
    if (settle.current) clearTimeout(settle.current);
    const go = () => {
      void sendCmd(serial, 'setVolume', { level }, `${level}%`).then(() => {
        // Keep showing the requested value until the robot's status catches up.
        settle.current = setTimeout(() => setDraft(null), 1500);
      });
    };
    if (immediate) go();
    else timer.current = setTimeout(go, 300);
  };
  const toggleMute = () => {
    if (muted || shown === 0) send(lastLevel[serial] && lastLevel[serial]! > 0 ? lastLevel[serial]! : 60, true);
    else {
      setLastLevel({ ...lastLevel, [serial]: shown });
      send(0, true);
    }
  };
  return (
    <div className="col" style={{ gap: 6 }}>
      <label className="range-field">
        <span className="top">
          {t('volume.speaker')}
          <span className="val">
            {shown}%
            {typeof vol?.voice === 'number' && vol.voice !== vol.media && <span className="muted"> · {t('volume.voice', { n: Math.round(vol.voice) })}</span>}
          </span>
        </span>
        <span className="row" style={{ gap: 8 }}>
          <button type="button" className={`btn icon sm${shown === 0 ? ' on' : ''}`} aria-pressed={shown === 0} aria-label={shown === 0 ? t('volume.unmute') : t('volume.mute')} title={shown === 0 ? t('volume.unmute') : t('volume.mute')} onClick={toggleMute} disabled={!online}>
            {shown === 0 ? <IconMuted /> : <IconSpeaker />}
          </button>
          <input type="range" className="grow" min={0} max={100} step={5} value={shown} disabled={!online} aria-label={t('volume.speaker')} onChange={(e) => send(Number(e.target.value))} />
        </span>
      </label>
      {!vol && online && <span className="muted" style={{ fontSize: 11 }}>{t('volume.unknown')}</span>}
    </div>
  );
}

/** Head tilt (PROTOCOL §16): slider over status.head.min..max (fallback 180–270), sent on release; presets. */
export function HeadPanel({ serial, online }: { serial: string; online: boolean }) {
  const t = useT();
  const head = useLive((s) => s.robots[serial]?.status?.head);
  const min = typeof head?.min === 'number' ? head.min : 180;
  const max = typeof head?.max === 'number' ? head.max : 270;
  const current = typeof head?.angle === 'number' ? Math.round(head.angle) : null;
  const [draft, setDraft] = useState<number | null>(null);
  const shown = draft ?? current ?? Math.round((min + max) / 2);
  const commit = async () => {
    if (draft === null) return;
    const angle = draft;
    await sendCmd(serial, 'setHead', { angle }, `${angle}°`);
    setDraft(null);
  };
  const preset = (p: HeadPreset) => {
    setDraft(null);
    void sendCmd(serial, 'setHead', { preset: p }, p);
  };
  return (
    <section className="panel">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h2 className="h2">{t('head.title')}</h2>
        <span className="mono" style={{ fontSize: 13 }}>
          {current !== null ? `${current}°` : '—'}
          {draft !== null && draft !== current && <span className="muted"> → {draft}°</span>}
        </span>
      </div>
      <label className="range-field">
        <span className="top">
          <span>{t('head.upDown')}</span>
          <span className="val">{shown}°</span>
        </span>
        <input
          type="range"
          min={min}
          max={max}
          step={1}
          value={shown}
          disabled={!online}
          aria-label={t('head.angle')}
          onChange={(e) => setDraft(Number(e.target.value))}
          onPointerUp={() => void commit()}
          onKeyUp={() => void commit()}
          onBlur={() => void commit()}
        />
        <span className="ends">
          <span>
            {min}° {t('head.up')}
          </span>
          <span>
            {t('head.down')} {max}°
          </span>
        </span>
      </label>
      <div className="head-presets">
        {PRESETS.map((p) => (
          <button key={p.id} type="button" className={`btn sm${current !== null && Math.abs(current - p.angle) <= 2 ? ' on' : ''}`} disabled={!online} onClick={() => preset(p.id)} title={`${p.angle}°`}>
            {t(`head.p.${p.id}`)}
          </button>
        ))}
      </div>
      <span className="muted" style={{ fontSize: 12, lineHeight: 1.4 }}>
        {t('head.hint')}
      </span>
    </section>
  );
}

/** LED colour + effect (`light {color, effect?}`; Off = `{color: null}`). */
export function LightControl({ serial, online, compact }: { serial: string; online: boolean; compact?: boolean }) {
  const t = useT();
  const c = useCatalog().data;
  const [color, setColor] = useState('#00FF00');
  const [effect, setEffect] = useState('');
  const run = (args: Record<string, unknown>) => void sendCmd(serial, 'light', args);
  return (
    <div className="row" style={{ alignItems: 'flex-end' }}>
      <label className="field">
        {t('quick.light')}
        <input type="color" value={color} onChange={(e) => setColor(e.target.value.toUpperCase())} style={{ width: 44, height: compact ? 32 : 40, padding: 2, borderRadius: 8, border: '1px solid var(--border-2)', background: 'var(--input)' }} />
      </label>
      <Select
        className="grow"
        size={compact ? 'sm' : 'md'}
        ariaLabel={t('quick.lightEffect')}
        value={effect}
        onChange={setEffect}
        options={[{ value: '', label: t('quick.noEffect') }, ...Object.entries(c?.lightEffects ?? {}).map(([k, v]) => ({ value: k, label: v, meta: k }))]}
      />
      <button type="button" className={`btn${compact ? ' sm' : ''}`} disabled={!online} onClick={() => run({ color, ...(effect ? { effect: Number(effect) } : {}) })}>
        {t('quick.on')}
      </button>
      <button type="button" className={`btn ghost${compact ? ' sm' : ''}`} disabled={!online} onClick={() => run({ color: null })}>
        {t('quick.off')}
      </button>
    </div>
  );
}
